import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Button,
  Divider,
  Skeleton,
  Avatar,
} from '@mui/material';
import {
  ArrowLeftCircle,
  ArrowRightCircle,
  ChevronDown,
  ChevronRight,
  Link2,
  Link2Off,
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  Ban,
  PlayCircle,
  Plus,
  X,
} from 'lucide-react';
import dayjs from 'dayjs';

import { Task, TaskStatus, TaskPriority } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';

// ============================================================================
// TYPES
// ============================================================================

interface TaskDependencyViewerProps {
  task: Task;
  allTasks: Task[];
  onTaskClick?: (task: Task) => void;
  onRemoveDependency?: (fromTaskId: string, toTaskId: string) => void;
  onAddDependency?: () => void;
  isLoading?: boolean;
  readonly?: boolean;
}

interface DependencyCardProps {
  task: Task;
  direction: 'incoming' | 'outgoing';
  onTaskClick?: (task: Task) => void;
  onRemove?: () => void;
  readonly?: boolean;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return <CheckCircle size={14} style={{ color: '#16a34a' }} />;
    case TaskStatus.IN_PROGRESS:
      return <PlayCircle size={14} style={{ color: '#4F46E5' }} />;
    case TaskStatus.BLOCKED:
      return <Ban size={14} style={{ color: '#f59e0b' }} />;
    case TaskStatus.CANCELLED:
      return <Ban size={14} style={{ color: '#ef4444' }} />;
    default:
      return <Circle size={14} style={{ color: '#64748b' }} />;
  }
};

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return '#16a34a';
    case TaskStatus.IN_PROGRESS:
      return '#4F46E5';
    case TaskStatus.BLOCKED:
      return '#f59e0b';
    case TaskStatus.CANCELLED:
      return '#ef4444';
    default:
      return '#64748b';
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.NOT_STARTED:
      return 'Not Started';
    case TaskStatus.IN_PROGRESS:
      return 'In Progress';
    case TaskStatus.COMPLETED:
      return 'Completed';
    case TaskStatus.BLOCKED:
      return 'Blocked';
    case TaskStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
};

// ============================================================================
// DEPENDENCY CARD COMPONENT
// ============================================================================

const DependencyCard: React.FC<DependencyCardProps> = ({
  task,
  direction,
  onTaskClick,
  onRemove,
  readonly = false,
}) => {
  const isOverdue =
    task.status !== TaskStatus.COMPLETED &&
    task.status !== TaskStatus.CANCELLED &&
    dayjs(task.dueDate).isBefore(dayjs());

  const isBlocking = direction === 'incoming' && task.status !== TaskStatus.COMPLETED;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: isBlocking ? 'warning.50' : 'background.paper',
        border: 1,
        borderColor: isBlocking ? 'warning.200' : 'grey.200',
        cursor: onTaskClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: isBlocking ? 'warning.100' : 'grey.50',
          '& .remove-btn': {
            opacity: 1,
          },
        },
      }}
      onClick={() => onTaskClick?.(task)}
    >
      {/* Direction Icon */}
      <Tooltip
        title={direction === 'incoming' ? 'This task blocks current task' : 'Current task blocks this task'}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: direction === 'incoming' ? 'warning.100' : 'info.100',
            color: direction === 'incoming' ? 'warning.dark' : 'info.dark',
          }}
        >
          {direction === 'incoming' ? (
            <ArrowLeftCircle size={16} />
          ) : (
            <ArrowRightCircle size={16} />
          )}
        </Box>
      </Tooltip>

      {/* Status Icon */}
      <Tooltip title={getStatusLabel(task.status)}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getStatusIcon(task.status)}
        </Box>
      </Tooltip>

      {/* Task Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            textDecoration:
              task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
            color:
              task.status === TaskStatus.COMPLETED
                ? 'text.secondary'
                : 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {task.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            Due: {dayjs(task.dueDate).format('MMM D')}
          </Typography>
          {isOverdue && (
            <Chip
              size="small"
              label="Overdue"
              sx={{
                height: 16,
                fontSize: '0.6rem',
                bgcolor: 'error.100',
                color: 'error.dark',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Priority */}
      <PriorityBadge priority={task.priority} size="small" />

      {/* Blocking Indicator */}
      {isBlocking && (
        <Tooltip title="Blocking - Complete this task first">
          <Chip
            size="small"
            icon={<AlertCircle size={12} />}
            label="Blocking"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              bgcolor: 'warning.100',
              color: 'warning.dark',
              '& .MuiChip-icon': {
                color: 'inherit',
              },
            }}
          />
        </Tooltip>
      )}

      {/* Completed Indicator */}
      {task.status === TaskStatus.COMPLETED && (
        <Tooltip title="Dependency resolved">
          <Chip
            size="small"
            icon={<CheckCircle size={12} />}
            label="Done"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              bgcolor: 'success.100',
              color: 'success.dark',
              '& .MuiChip-icon': {
                color: 'inherit',
              },
            }}
          />
        </Tooltip>
      )}

      {/* Remove Button */}
      {!readonly && onRemove && (
        <IconButton
          size="small"
          className="remove-btn"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            opacity: 0,
            transition: 'opacity 0.15s ease',
            p: 0.5,
            '&:hover': {
              bgcolor: 'error.100',
              color: 'error.main',
            },
          }}
        >
          <X size={14} />
        </IconButton>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TaskDependencyViewer: React.FC<TaskDependencyViewerProps> = ({
  task,
  allTasks,
  onTaskClick,
  onRemoveDependency,
  onAddDependency,
  isLoading = false,
  readonly = false,
}) => {
  const [expandedBlocking, setExpandedBlocking] = useState(true);
  const [expandedBlocked, setExpandedBlocked] = useState(true);

  // Get blocking tasks (tasks that block this task)
  const blockingTasks = allTasks.filter((t) =>
    task.blockedByTaskIds?.includes(t.id)
  );

  // Get blocked tasks (tasks that this task blocks)
  const blockedTasks = allTasks.filter((t) =>
    task.blockingTaskIds?.includes(t.id)
  );

  // Calculate stats
  const unresolvedBlockers = blockingTasks.filter(
    (t) => t.status !== TaskStatus.COMPLETED
  ).length;

  const totalDependencies = blockingTasks.length + blockedTasks.length;

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width={120} />
            <Skeleton variant="rounded" height={60} sx={{ mt: 1 }} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link2 size={18} style={{ color: '#64748b' }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Dependencies
          </Typography>
          {totalDependencies > 0 && (
            <Chip
              size="small"
              label={totalDependencies}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            />
          )}
          {unresolvedBlockers > 0 && (
            <Chip
              size="small"
              icon={<AlertCircle size={12} />}
              label={`${unresolvedBlockers} blocking`}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: 'warning.100',
                color: 'warning.dark',
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              }}
            />
          )}
        </Box>
        {!readonly && (
          <Button
            size="small"
            startIcon={<Plus size={14} />}
            onClick={onAddDependency}
            sx={{ textTransform: 'none' }}
          >
            Add Dependency
          </Button>
        )}
      </Box>

      {/* No Dependencies */}
      {totalDependencies === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Link2Off size={32} style={{ color: '#9ca3af', marginBottom: 8 }} />
          <Typography variant="body2" color="text.secondary">
            No dependencies
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Add dependencies to track task relationships
          </Typography>
          {!readonly && (
            <Button
              size="small"
              startIcon={<Plus size={14} />}
              onClick={onAddDependency}
              sx={{ mt: 1.5, textTransform: 'none' }}
            >
              Add Dependency
            </Button>
          )}
        </Box>
      )}

      {/* Blocking Tasks Section (Incoming) */}
      {blockingTasks.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box
            onClick={() => setExpandedBlocking(!expandedBlocking)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              mb: 1,
              '&:hover': { opacity: 0.8 },
            }}
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              {expandedBlocking ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </IconButton>
            <ArrowLeftCircle size={16} style={{ color: '#f59e0b' }} />
            <Typography variant="body2" fontWeight={500}>
              Blocked By
            </Typography>
            <Chip
              size="small"
              label={`${blockingTasks.filter((t) => t.status === TaskStatus.COMPLETED).length}/${blockingTasks.length}`}
              sx={{
                height: 18,
                fontSize: '0.65rem',
              }}
            />
            {unresolvedBlockers > 0 && (
              <Typography variant="caption" color="warning.main">
                ({unresolvedBlockers} unresolved)
              </Typography>
            )}
          </Box>
          <Collapse in={expandedBlocking}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pl: 3,
              }}
            >
              {blockingTasks.map((blockingTask) => (
                <DependencyCard
                  key={blockingTask.id}
                  task={blockingTask}
                  direction="incoming"
                  onTaskClick={onTaskClick}
                  onRemove={
                    !readonly && onRemoveDependency
                      ? () => onRemoveDependency(blockingTask.id, task.id)
                      : undefined
                  }
                  readonly={readonly}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Blocked Tasks Section (Outgoing) */}
      {blockedTasks.length > 0 && (
        <Box>
          <Box
            onClick={() => setExpandedBlocked(!expandedBlocked)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              mb: 1,
              '&:hover': { opacity: 0.8 },
            }}
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              {expandedBlocked ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </IconButton>
            <ArrowRightCircle size={16} style={{ color: '#4F46E5' }} />
            <Typography variant="body2" fontWeight={500}>
              Blocking
            </Typography>
            <Chip
              size="small"
              label={blockedTasks.length}
              sx={{
                height: 18,
                fontSize: '0.65rem',
              }}
            />
          </Box>
          <Collapse in={expandedBlocked}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pl: 3,
              }}
            >
              {blockedTasks.map((blockedTask) => (
                <DependencyCard
                  key={blockedTask.id}
                  task={blockedTask}
                  direction="outgoing"
                  onTaskClick={onTaskClick}
                  onRemove={
                    !readonly && onRemoveDependency
                      ? () => onRemoveDependency(task.id, blockedTask.id)
                      : undefined
                  }
                  readonly={readonly}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Dependency Summary */}
      {totalDependencies > 0 && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {unresolvedBlockers > 0 ? (
              <>
                <AlertCircle
                  size={12}
                  style={{
                    verticalAlign: 'middle',
                    marginRight: 4,
                    color: '#f59e0b',
                  }}
                />
                This task is blocked by {unresolvedBlockers} unresolved{' '}
                {unresolvedBlockers === 1 ? 'dependency' : 'dependencies'}.
                Complete them first.
              </>
            ) : blockingTasks.length > 0 ? (
              <>
                <CheckCircle
                  size={12}
                  style={{
                    verticalAlign: 'middle',
                    marginRight: 4,
                    color: '#16a34a',
                  }}
                />
                All blocking dependencies resolved. This task is ready to work on.
              </>
            ) : (
              <>
                This task blocks {blockedTasks.length}{' '}
                {blockedTasks.length === 1 ? 'other task' : 'other tasks'}.
              </>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TaskDependencyViewer;
